import { useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PiX } from 'react-icons/pi'

import { Modal } from 'components/UI/Modal'
import apiService from 'services/api'
import { useUsers } from 'services/users'
import FbButton from 'components/FbUI/FbButton'
import { CompanyTypeSlug } from 'models/companies'
import {
  CampaignFormFieldsV2,
  type CampaignSelectOption,
  buildCampaignOwnerOptions,
  getColorOption,
} from './CampaignFormFieldsV2'
interface CreateCampaignModalV2Props {
  show: boolean
  onHide: () => void
  /** Called after successful creation instead of navigating to the campaign detail page. */
  onCreated?: (campaign: {
    id: number
    name: string
    color: string
    typeSlug?: string
  }) => void
  /** Pre-fill the type when creating from a targets table. */
  defaultTypeSlug?: CompanyTypeSlug
  /** Pre-fill the name when launching from an inline picker (e.g. the
   *  AttributePickerModal's "Create new" affordance). Synced when the
   *  modal transitions from closed to open so re-opens don't carry
   *  over stale typed names. */
  defaultName?: string
}

export function CreateCampaignModalV2({
  show,
  onHide,
  onCreated,
  defaultTypeSlug,
  defaultName,
}: CreateCampaignModalV2Props) {
  const navigate = useNavigate()
  const api = apiService()
  const createCampaignMutation = api.useCreateCampaignV2()
  const { data: users = [], isLoading: isUsersLoading } = useUsers(undefined, {
    query: { enabled: true },
  })
  const { data: products = [], isLoading: isProductsLoading } =
    api.useGetProducts()
  const { data: categories, isLoading: isCampaignTypesLoading } =
    api.useGetActiveCompanyCategoriesAndTypes(false)

  const [name, setName] = useState(defaultName ?? '')
  // Sync the name field when the modal opens with a new defaultName.
  // Using a "previous show" ref pattern avoids overwriting user edits
  // mid-session while still picking up handoffs from a parent picker.
  const prevShowRef = useRef(show)
  if (show && !prevShowRef.current) {
    setName(defaultName ?? '')
  }
  prevShowRef.current = show
  const [campaignTypeSlug, setCampaignTypeSlug] =
    useState<CompanyTypeSlug | null>(defaultTypeSlug ?? null)
  const [productId, setProductId] = useState<number | null>(null)
  const [launchDateType, setLaunchDateType] = useState<'set' | 'skip'>('set')
  const [launchDate, setLaunchDate] = useState('')
  const [ownerId, setOwnerId] = useState<number | null>(null)
  const [notificationRecipientIds, setNotificationRecipientIds] = useState<
    number[]
  >([])
  const [description, setDescription] = useState('')
  const [color, setColor] = useState(getColorOption())

  const canSubmit = name.trim().length > 0

  const ownerOptions = useMemo(() => buildCampaignOwnerOptions(users), [users])
  // Recipients aren't an ownership assignment, so limited users stay selectable.
  const recipientOptions = useMemo(
    () => buildCampaignOwnerOptions(users, { excludeLimited: false }),
    [users]
  )

  const campaignTypeOptions = useMemo<
    CampaignSelectOption<CompanyTypeSlug>[]
  >(() => {
    const options: CampaignSelectOption<CompanyTypeSlug>[] = []

    categories?.forEach((category) => {
      category.sub_categories.forEach((subCategory) => {
        options.push({
          value: subCategory.slug,
          label: `${category.name} - ${subCategory.name}`,
        })
      })
    })

    return options
  }, [categories])

  const productOptions = useMemo<CampaignSelectOption<number>[]>(
    () =>
      products.map((product) => ({
        value: product.id,
        label: product.name,
      })),
    [products]
  )

  const handleSubmit = async () => {
    if (!canSubmit) return

    try {
      const result = await createCampaignMutation.mutateAsync({
        name: name.trim(),
        type_slug: campaignTypeSlug ?? undefined,
        product_id: productId,
        color: color.background,
        description: description.trim(),
        launch_date: launchDateType === 'skip' ? null : launchDate || null,
        owner: ownerId,
        notification_recipient_ids: notificationRecipientIds,
      })

      resetForm()
      onHide()
      if (onCreated) {
        onCreated({
          id: result.id,
          name: result.name,
          color: result.color,
          typeSlug: campaignTypeSlug ?? undefined,
        })
      } else {
        navigate(`/campaigns/${result.id}/detail`)
      }
    } catch {
      // Mutation handles error display via toast
    }
  }

  const handleClose = () => {
    if (createCampaignMutation.isPending) return
    resetForm()
    onHide()
  }

  function resetForm() {
    setName('')
    setCampaignTypeSlug(defaultTypeSlug ?? null)
    setProductId(null)
    setLaunchDate('')
    setLaunchDateType('set')
    setOwnerId(null)
    setNotificationRecipientIds([])
    setDescription('')
    setColor(getColorOption())
  }

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <div className="flex items-center justify-between border-b border-line px-6 py-4">
        <div>
          <h2 className="text-lg font-medium text-fg">Create Campaign</h2>
          <p className="mt-1 text-sm text-fg-secondary">
            Start with the campaign basics. Targets and contact requests will
            come later once target lists are supported.
          </p>
        </div>
        <button
          type="button"
          onClick={handleClose}
          disabled={createCampaignMutation.isPending}
          className="flex size-8 items-center justify-center rounded-lg transition-colors hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-50"
          aria-label="Close create campaign modal"
        >
          <PiX className="size-4 text-fg-muted" />
        </button>
      </div>

      <Modal.Body className="px-6 pb-5 pt-3">
        <CampaignFormFieldsV2
          name={name}
          onNameChange={setName}
          campaignTypeOptions={campaignTypeOptions}
          campaignTypeValue={campaignTypeSlug}
          onCampaignTypeChange={setCampaignTypeSlug}
          campaignTypeLoading={isCampaignTypesLoading}
          launchDate={launchDate}
          onLaunchDateChange={setLaunchDate}
          launchDateType={launchDateType}
          onLaunchDateTypeChange={setLaunchDateType}
          ownerOptions={ownerOptions}
          ownerValue={ownerId}
          onOwnerChange={setOwnerId}
          ownerLoading={isUsersLoading}
          recipientOptions={recipientOptions}
          productOptions={productOptions}
          productValue={productId}
          onProductChange={setProductId}
          productLoading={isProductsLoading}
          description={description}
          onDescriptionChange={setDescription}
          color={color}
          onColorChange={setColor}
          notificationRecipientIds={notificationRecipientIds}
          onNotificationRecipientsChange={setNotificationRecipientIds}
        />
      </Modal.Body>

      <Modal.Footer className="border-t border-line px-6 py-4">
        <div className="flex w-full justify-end gap-3">
          <FbButton
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={createCampaignMutation.isPending}
          >
            Cancel
          </FbButton>
          <FbButton
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            loading={createCampaignMutation.isPending}
          >
            Create Campaign
          </FbButton>
        </div>
      </Modal.Footer>
    </Modal>
  )
}
